import * as Sentry from "@sentry/react";

export type LocationSuggestion = {
  id: string;
  name: string;
  address: string;
  placeId: string;
  location?: {
    latitude: number;
    longitude: number;
  };
  types?: string[];
  isCurrentLocation?: boolean;
};

// Re-export from centralized Google API client
export { searchPlaces } from "./googleApi.ts";

/**
 * Check current geolocation permission state
 */
export const getLocationPermissionState = async (): Promise<PermissionState> => {
  try {
    if (!navigator.permissions) {
      // Fallback for browsers that don't support permissions API
      return "prompt";
    }
    const result = await navigator.permissions.query({ name: "geolocation" });
    return result.state;
  } catch (error) {
    Sentry.captureException(error, {
      tags: {
        component: "LocationPermissions",
        function: "getLocationPermissionState",
      },
    });
    return "prompt";
  }
};

/**
 * Get the current location using browser geolocation
 */
export const getCurrentLocation = async (): Promise<LocationSuggestion> => {
  // Check permission state first
  const permissionState = await getLocationPermissionState();

  return new Promise((resolve, reject) => {
    if (!navigator.geolocation) {
      const error = new Error("Geolocation is not supported by this browser");
      Sentry.captureException(error, {
        tags: {
          component: "LocationPermissions",
          function: "getCurrentLocation",
          reason: "geolocation_not_supported",
        },
        extra: {
          userAgent: navigator.userAgent,
          permissionState,
        },
      });
      reject(error);
      return;
    }

    // Check if we're on HTTPS or localhost
    const isSecureContext = window.isSecureContext || location.hostname === "localhost";
    if (!isSecureContext) {
      const error = new Error(
        "Geolocation requires HTTPS. Please access this page over a secure connection.",
      );
      Sentry.captureException(error, {
        tags: {
          component: "LocationPermissions",
          function: "getCurrentLocation",
          reason: "insecure_context",
        },
        extra: {
          protocol: location.protocol,
          hostname: location.hostname,
          permissionState,
        },
      });
      reject(error);
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        const { latitude, longitude } = position.coords;

        // Create a current location suggestion with coordinates
        // The actual location name will be resolved when the user selects this option
        const currentLocation: LocationSuggestion = {
          id: "location",
          name: "Current location",
          address: "Using your current location",
          placeId: "location",
          location: { latitude, longitude },
          isCurrentLocation: true,
        };

        // Log successful location access
        Sentry.addBreadcrumb({
          message: "Location access granted",
          category: "location",
          level: "info",
          data: {
            accuracy: position.coords.accuracy,
            permissionState,
          },
        });

        resolve(currentLocation);
      },
      (error) => {
        let errorMessage: string;
        let errorReason: string;

        switch (error.code) {
          case error.PERMISSION_DENIED:
            errorMessage =
              "Location access denied. Please enable location permissions in your browser settings and try again.";
            errorReason = "permission_denied";
            break;
          case error.POSITION_UNAVAILABLE:
            errorMessage =
              "Your location is currently unavailable. Please check your device's location settings.";
            errorReason = "position_unavailable";
            break;
          case error.TIMEOUT:
            errorMessage = "Location request timed out. Please try again.";
            errorReason = "timeout";
            break;
          default:
            errorMessage =
              "Unable to access your location. Please try again or enter your location manually.";
            errorReason = "unknown_error";
        }

        const enhancedError = new Error(errorMessage);

        Sentry.captureException(enhancedError, {
          tags: {
            component: "LocationPermissions",
            function: "getCurrentLocation",
            reason: errorReason,
          },
          extra: {
            geolocationError: {
              code: error.code,
              message: error.message,
            },
            permissionState,
            userAgent: navigator.userAgent,
            isSecureContext,
          },
        });

        reject(enhancedError);
      },
      {
        enableHighAccuracy: true,
        timeout: 15000, // Increased timeout to 15 seconds
        maximumAge: 60000, // Cache for 1 minute
      },
    );
  });
};
